import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Landing from './pages/Landing';
import Biosite from './pages/Biosite';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminEditor from './pages/AdminEditor';
import AdminSettings from './pages/AdminSettings';
import { isAuthenticated } from './api';
import { Toaster } from './components/ui/toaster';

// -------- Error boundary (top-level) --------
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('App crash:', error, info);
  }
  reset = () => { this.setState({ error: null }); };
  render() {
    if (this.state.error) {
      const msg = (this.state.error && this.state.error.message) || 'Erro desconhecido';
      return (
        <div style={{ minHeight: '100vh', background: '#faf7f2', color: '#111', padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif' }}>
          <div style={{ maxWidth: 440, width: '100%', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 16, padding: 24, textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, background: '#111', borderRadius: 12, color: '#fff', display: 'grid', placeItems: 'center', margin: '0 auto 12px', fontSize: 22 }}>!</div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Algo deu errado</h1>
            <p style={{ margin: '8px 0 20px', color: '#555', fontSize: 14 }}>Tente recarregar a página ou voltar para o início.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => { this.reset(); window.location.reload(); }} style={{ padding: '10px 16px', background: '#111', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Recarregar</button>
              <a href="/" style={{ padding: '10px 16px', background: '#fff', color: '#111', border: '1px solid #e5e5e5', borderRadius: 8, textDecoration: 'none', fontWeight: 600 }}>Página inicial</a>
            </div>
            <details style={{ marginTop: 20, color: '#777', fontSize: 12, textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer' }}>Detalhes técnicos</summary>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 8 }}>{msg}</pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// -------- SPA redirect handler (reads from sessionStorage after 404 fallback) --------
function SpaRedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('__spa_redirect__');
      if (stored && stored !== location.pathname + location.search + location.hash) {
        sessionStorage.removeItem('__spa_redirect__');
        navigate(stored, { replace: true });
      } else if (stored) {
        sessionStorage.removeItem('__spa_redirect__');
      }
    } catch { /* ignore */ }
    // Run only on mount - handler is global
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function ProtectedRoute({ children }) {
  if (!isAuthenticated()) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <SpaRedirectHandler />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/edit/:slug" element={<ProtectedRoute><AdminEditor /></ProtectedRoute>} />
            <Route path="/:slug" element={<Biosite />} />
            {/* Catch-all so unknown paths never show a blank page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </ErrorBoundary>
  );
}

export default App;
